import React from 'react';

import { Divider } from '@bringg/react-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { getLocalizedDistanceMessage } from '@bringg/frontend-utils';

import useStore from '../../stores/helpers/use-context';
import type { KPIsTranslations } from '../routes-planner/translations';
import { OptimizationKpiData } from '../../stores/kpi/optimization-kpi-store';
import { dateUtils } from '../../utils/date-utils';

import styles from './planner-kpis.module.scss';

interface RowData {
	title: string;
	value: string;
	dataTestId: string;
	position: number;
	className?: string;
}

interface Props {
	translations: KPIsTranslations & { miles: string; km: string };
	countryCode: string;
}

const PlannerKpis = ({ countryCode, translations }: Props) => {
	const { optimizationKpiStore } = useStore();
	const { optimizationKpiData: kpiData, kpiConfiguration } = optimizationKpiStore;

	const convertKpiDataToRowData = (data: OptimizationKpiData): RowData[] => {
		const result = data
			? [
					{
						title: `${translations.unassigned}/${translations.assigned}`,
						value: `${data.unassignedCount}/${data.assignedCount}`,
						dataTestId: 'assigned-unassigned-count',
						position: 0
					},
					{
						title: translations.routesDuration,

						value: `${dateUtils.msToDuration(data.totalRoutesDuration, 'hh:mm')}`,
						dataTestId: 'total-routes-duration',
						position: 2
					},
					{
						title: translations.routesDistance,
						value: getLocalizedDistanceMessage(data.totalRoutesDistance * 1000, countryCode, {
							miles: translations.miles,
							km: translations.km
						}),
						dataTestId: 'total-routes-distance',
						position: 3
					},
					{
						title: translations.stopsPerHour,
						value: data.stopsPerHour.toString(),
						dataTestId: 'stops-per-hour',
						position: 4
					},
					{
						title: translations.intersections,
						value: data.intersections.toString(),
						dataTestId: 'intersections',
						position: 5
					}
			  ]
			: [];
		if (data && kpiConfiguration.isFairnessEnabled) {
			result.push({
				title: translations.timeFairness,
				value: `${Math.round(data.timeFairness * 100)}%`,
				dataTestId: 'time-fairness',
				position: 1
			});
		}

		return result.sort((a, b) => a.position - b.position);
	};

	const kpiTitleAndValue = (rowData: RowData) => {
		return (
			<div className={styles.kpiTitleAndValueContainer} key={rowData.position}>
				<span className={classNames(styles.kpiTitle, rowData.className)}>{rowData.title}:</span>
				<span className={classNames(styles.kpiValue, rowData.className)} data-test-id={rowData.dataTestId}>
					{rowData.value}
				</span>
			</div>
		);
	};

	const kpisRow = (rowData: RowData[]) => {
		return <div className={styles.kpisValuesContainer}>{rowData.map(data => kpiTitleAndValue(data))}</div>;
	};

	const original = convertKpiDataToRowData(kpiData?.original);
	const manual = convertKpiDataToRowData(kpiData?.manual);

	return (
		<div className={styles.plannerKPIs}>
			<Divider className={styles.kpisDivider} />
			<div className={styles.kpisRowContainer}>
				<span className={styles.kpisMainTitle}>{translations.kpisByOriginal}</span>
				{original && kpisRow(original)}
			</div>
			<Divider className={styles.kpisDivider} />
			<div className={styles.kpisRowContainer}>
				<span className={styles.kpisMainTitle}>{translations.kpisByManual}</span>
				{manual && kpisRow(manual)}
			</div>
		</div>
	);
};

export default observer(PlannerKpis);
